<template>
    <div v-click-outside="onClickOutside" class="c-burger-menu">
        <div id="menu-toggle">
            <input
                id="burger-menu-input"
                v-model="isBurgerOpen"
                type="checkbox"
                class="burger-state"
            />

            <span class="burger-line" />
            <span class="burger-line" />
            <span class="burger-line" />

            <ul :id="menuDomId" class="burger-menu-list">
                <slot name="before-default" />
                <slot>
                    <template v-if="!user">
                        <li>
                            <router-link :to="{ name: 'auth.register' }">
                                {{ $t('auth.signUp') }}
                            </router-link>
                        </li>
                        <li>
                            <router-link :to="{ name: 'auth.login' }">
                                {{ $t('auth.logIn') }}
                            </router-link>
                        </li>
                    </template>
                    <template v-else-if="!hideUserInfo">
                        <li>
                            <UserInfo :in-burger-menu="true" />
                        </li>
                    </template>
                </slot>
                <slot name="after-default" />
            </ul>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import vClickOutside from 'click-outside-vue3';
import UserInfo from './UserInfo.vue';

export default {
    components: { UserInfo },
    directives: {
        clickOutside: vClickOutside.directive,
    },
    props: {
        hideUserInfo: {
            default: false,
        },
    },
    data() {
        return {
            isBurgerOpen: false,
            menuDomId: 'menu',
        };
    },
    computed: {
        ...mapGetters(['user']),
    },
    watch: {
        isBurgerOpen(newVal) {
            newVal === true
                ? (document.body.style.overflow = 'hidden')
                : (document.body.style.overflow = 'auto');
        },
    },
    beforeUnmount() {
        const elem = document.getElementById(this.menuDomId);

        if (elem) {
            elem.removeEventListener('click', this.handleMenuElementClick);
        }

        document.body.style.overflow = 'auto';
    },
    mounted() {
        const elem = document.getElementById(this.menuDomId);

        if (elem) {
            elem.addEventListener('click', this.handleMenuElementClick);
        }
    },
    methods: {
        onClickOutside() {
            this.isBurgerOpen = false;
        },
        handleMenuElementClick(event) {
            if (event.target.nodeName === 'A') {
                this.isBurgerOpen = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.c-burger-menu {
    z-index: 1000;

    @include mq($w-min-desktop, min) {
        display: none;
    }

    @include mq($w-max-tablet, max) {
        margin-left: auto;
        height: 100%;
        align-items: center;
        display: flex;

        #menu-toggle {
            z-index: 1;
            -webkit-user-select: none;
            user-select: none;
            position: relative;
            width: 38px;
            min-height: 38px;
            padding-right: 20px;
            margin-top: 20px;

            .burger-state {
                display: block;
                width: 33px;
                height: 24px;
                position: absolute;
                top: 0;
                left: 0;
                cursor: pointer;
                opacity: 0; /* hide this */
                z-index: 2; /* and place it over the hamburger */
                -webkit-touch-callout: none;
                margin: 0;

                &:checked {
                    width: 20px;
                    height: 16px;
                }
            }

            .burger-line {
                display: block;
                width: 32px;
                height: 4px;
                margin: 0 5px 5px 0;
                position: relative;
                background: rgba(255, 255, 255, 0.8);
                border-radius: 3px;
                z-index: 1;
                transform-origin: 4px 0;
                transition:
                    transform 0.5s cubic-bezier(0.77, 0.2, 0.05, 1),
                    background 0.5s cubic-bezier(0.77, 0.2, 0.05, 1),
                    opacity 0.55s ease;

                &:first-child {
                    transform-origin: 0 0;
                }

                &:nth-last-child(2) {
                    transform-origin: 0 100%;
                }
            }

            .burger-state:checked ~ .burger-line {
                opacity: 1;
                transform: rotate(45deg) translate(3px, -5px);
                background: rgba(255, 255, 255, 0.8);
            }

            .burger-state:checked ~ .burger-line:nth-last-child(3) {
                opacity: 0;
                transform: scale(0.2, 0.2);
            }

            .burger-state:checked ~ .burger-line:nth-last-child(2) {
                transform: rotate(-45deg) translate(5px, 4px);
            }
        }

        :deep(.burger-menu-list) {
            display: none;
            position: fixed;
            top: calc(60px + var(--safe-area-inset-top, 0));
            // .homepage__header height - 4px border-top
            right: 0;
            width: 300px;
            margin: 0;
            padding: 0;
            list-style-type: none;
            overflow-y: auto;
            overflow-x: hidden;
            background: $quinary-theme-color;
            box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
            border-top: 4px solid #f08a4f;

            @include mq($w-max-phone, max) {
                width: 100vw;
                top: calc(64px + var(--safe-area-inset-top, 0));
                // .homepage__header height
                bottom: 0;
            }

            @media screen and (min-width: $w-min-tablet) and (max-width: $w-max-tablet) {
                max-height: -moz-available;
                max-height: -webkit-fill-available;
                max-height: stretch;
            }
            li {
                &:hover {
                    background: $quaternary-theme-color;
                }

                > a {
                    padding-left: 25px;
                    font-family: $main-font-family;
                    font-weight: 500;

                    &:hover {
                        background: $quaternary-theme-color;
                    }
                }

                .c-dropdown-btn {
                    padding: 0;
                    background-color: $quinary-theme-color;

                    &--dropdown {
                        position: relative;
                    }

                    .btn,
                    button {
                        color: $main-font-color;
                        border: 0;
                        cursor: default;
                        font-size: 1.25rem;
                        font-family: $main-font-family;
                        margin-left: 1.5rem;

                        &:hover {
                            cursor: default;
                            background-color: $quaternary-theme-color;
                        }
                    }

                    > button,
                    > button:hover {
                        background-color: $quaternary-theme-color;
                        width: 100%;
                        margin: 0;
                        text-align: left;
                        padding: 25px;
                        display: block;
                        outline: none;
                    }

                    li {
                        .icon {
                            margin-left: -30px;
                            margin-right: 5px;
                        }

                        a {
                            text-decoration: none;
                            padding: 25px 50px;
                            width: 100%;
                            font-family: $main-font-family;
                        }

                        &:hover {
                            background: $quaternary-theme-color;
                        }
                    }

                    &.header-link {
                        border: none;
                        margin: 0;
                        opacity: 1;
                    }

                    &--dropdown {
                        margin: 0;

                        ul li {
                            background-color: $quinary-theme-color;
                        }
                    }
                }
            }

            > li {
                margin: 0;
                padding: 0;
                position: relative;
                z-index: 1;

                .user {
                    color: $main-font-color;
                }

                > * {
                    color: $main-font-color;
                    display: block;
                    padding: 20px 0;
                    font-size: 22px;
                }

                a {
                    &:hover {
                        text-decoration: none;
                    }
                }
            }
        }

        #menu-toggle .burger-state:checked ~ ul {
            display: block;
        }
    }
}
</style>
<style lang="scss">
.host-container .c-burger-menu .burger-menu-list {
    @media screen and (min-width: $w-min-tablet) and (max-width: $w-max-tablet) {
        top: 60px;
    }
}
</style>
